<template>
  <!-- Appointment Card Content -->
  <div 
    v-for="(attachment, index) in messageData.attachments" 
    :key="index" 
    :class="bubbleContentClass" 
    :style="{ marginBottom: index < messageData.attachments.length - 1 ? '8px' : '0' }"
    @mouseenter="isHovered = true" 
    @mouseleave="isHovered = false"
  >
    <div 
      class="appointment-card-content"
    >
      <!-- Appointment Header -->
      <div class="appointment-header">
        <div
          class="appointment-icon flex items-center justify-center bg-[#f2f2f2] rounded-[5px] w-[48px] h-[48px]"
        >
          <img :src="require('@/assets/MyPatient/appointment_card.svg')" alt="Appointment" class="w-[24px] h-[24px]" />
        </div>
        <div class="appointment-info">
          <div class="appointment-title-row">
            <div class="appointment-title" :class="{ 'appointment-title-hovered': isHovered }">
              Appointment: {{ getAppointmentData(attachment).title || 'Appointment' }}
            </div>
            <img
              v-if="getAppointmentData(attachment).isRecurring"
              :src="require('@/assets/MyPatient/sync_icon.svg')"
              alt="Recurring"
              class="sync-icon"
            />
          </div>
          <div class="appointment-details">
            <div class="appointment-time">Appointment Time: {{ getAppointmentData(attachment).time || 'Time not specified' }}</div>
            <div class="appointment-location">
              Location: 
              <a v-if="getAppointmentData(attachment).meetingLink" :href="getAppointmentData(attachment).meetingLink" class="meeting-link" target="_blank" rel="noopener noreferrer">
                {{ getAppointmentData(attachment).location || 'N/A' }}
              </a>
              <span v-else>{{ getAppointmentData(attachment).location || 'N/A' }}</span>
            </div>
            <div class="appointment-status-row">
              <span class="appointment-status">Status: {{ getAppointmentData(attachment).status || 'N/A' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Appointment Description -->
      <div class="appointment-description" style="padding-left: 52px">
        {{ getAppointmentData(attachment).description || 'No description provided' }}
      </div>

      <!-- Appointment Actions -->
      <div class="appointment-actions">
        <button class="see-details-btn" @click="seeDetail(attachment)">
          <img
            src="@/assets/MyPatient/detail_icon.svg"
            alt="Details"
            class="w-[18px] h-[18px]"
          />
          <span>See Details</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MessageCardAppointment",
  props: {
    messageData: {
      type: Object,
      required: true,
    },
    isSent: {
      type: Boolean,
      default: false,
    },
    rc_user_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      fakeAdminId: 'abcded',
      isHovered: false
    };
  },
  computed: {
    isBotMessage() {
      return this.messageData.u._id === 'oafKicny6TymdzNxQ' || 
             this.messageData.u._id === 'EuMKAsjDZTsLc74Kw';
    },
    
    bubbleContentClass() {
      if (this.isBotMessage) {
        return 'bubble-content not-user-bubble appointment-card-bubble';
      }
      return 'bubble-content appointment-card-bubble';
    },

  },
  methods: {
    getAppointmentData(attachment) {
      // The attachment object contains addon_column with appointment details
      const addonColumn = attachment?.addon_column;
      
      if (!addonColumn) {
        return {
          title: 'Appointment',
          time: 'Time not specified',
          recurrence: null,
          isRecurring: false,
          location: 'N/A',
          meetingLink: null,
          status: 'N/A',
          description: 'No description provided'
        };
      }

      // Format time from start_time and end_time in addon_column
      const startTime = addonColumn.start_time;
      const endTime = addonColumn.end_time;
      let formattedTime = 'Time not specified';
      
      if (startTime && endTime) {
        const start = new Date(startTime);
        const end = new Date(endTime);
        const options = { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' };
        formattedTime = `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      }

      // Determine status from is_canceled
      const isCanceled = addonColumn.is_canceled;
      const status = isCanceled ? 'Canceled' : 'Scheduled';

      const data = {
        title: addonColumn.event_title || 'Appointment',
        time: formattedTime,
        recurrence: addonColumn.rrule,
        isRecurring: !!addonColumn.rrule,
        location: addonColumn.location || 'N/A',
        meetingLink: addonColumn.external_url || null,
        status: status,
        description: addonColumn.notes || 'No description provided'
      };
      
      return data;
    },
    
    formatAppointmentTime(attachment) {
      const appointmentData = this.getAppointmentData(attachment);
      if (!appointmentData.time) return 'N/A';
      
      const time = appointmentData.time;
      const recurrence = appointmentData.recurrence;
      
      if (recurrence) {
        return `${time}, ${recurrence}`;
      }
      
      return time;
    },

    seeDetail(attachment) {
      const addonColumn = attachment?.addon_column;
      if (!addonColumn || !addonColumn.event_id) {
        console.warn('No appointment data found in attachment');
        return;
      }

      // Format appointment data to match the expected structure for handleEditAppointment
      const appointmentData = {
        event_id: addonColumn.event_id,
        event_title: addonColumn.event_title,
        start_time: addonColumn.start_time,
        end_time: addonColumn.end_time,
        notes: addonColumn.notes,
        event_type: addonColumn.event_type,
        location: addonColumn.location,
        external_url: addonColumn.external_url,
        is_canceled: addonColumn.is_canceled,
        rrule: addonColumn.rrule,
        participants: addonColumn.v2_event_participant || [],
        patient_ids: addonColumn.v2_event_participant
          ?.filter(p => p.role === 'patient')
          .map(p => p.v2_calendar?.calendar_owner_id)
          .filter(Boolean) || []
      };

      // Emit event to parent component
      this.$emit('edit-appointment', appointmentData);
    },
  },
};
</script>

<style scoped>
/* Appointment Card Specific Styles */
.appointment-card-bubble {
  background-color: #f9fbfd;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  min-width: 480px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.appointment-card-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.appointment-header {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.appointment-icon {
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.appointment-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.appointment-title-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.appointment-title {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #374957;
  letter-spacing: 0.1px;
  transition: color 0.3s ease;
}

.appointment-title-hovered {
  color: #004897 !important;
}

.sync-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.appointment-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.appointment-time,
.appointment-location,
.appointment-status-row {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
}

.meeting-link {
  color: #004879;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  cursor: pointer;
}

.meeting-link:hover {
  opacity: 0.8;
}

.appointment-description {
  margin: 8px 0;
  overflow: hidden;
  color: var(--TrueBlack, #333);
  text-overflow: ellipsis;
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  padding-top: 0;
  border-top: none;
  transition: all 0.3s ease;
}

.appointment-description.not-hovered {
  border-top: 1px dashed var(--DividerGray, #c4c4c4);
  padding-top: 12px;
  margin-top: 4px;
}

.appointment-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  border-top: 1px dashed var(--DividerGray, #c4c4c4);
  padding-top: 12px;
  gap: 16px;
}

.see-details-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: "Lato", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #1e4670;
  text-decoration: underline;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}

.see-details-btn:hover {
  opacity: 0.8;
}

.see-details-btn span {
  white-space: nowrap;
}

/* Bubble content base styles */
.bubble-content {
  padding: 16px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  position: relative;
  color: #000000;
  border: 1px solid var(--DividerGray, #C4C4C4);
}

.not-user-bubble {
  background-color: #f9fbfd;
  color: #333333;
  border-radius: 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .appointment-card-bubble {
    padding: 12px 16px;
  }

  .appointment-description {
    padding-left: 0;
  }
}

/* Vue transition animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
